/* eslint-disable no-else-return */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useWatch, useFormContext, useFormState } from "react-hook-form";
import classNames from "classnames";
import { formatDistance } from "date-fns";
import debounce from "lodash.debounce";
import { useMutation, gql } from "@apollo/client";
import { Check, Eye } from "../Icons/general-interface-1";
import styles from "./index.module.scss";
import { Text } from "../Type/index";
import { sanitizeUpdateClientFormData } from "../../utils/forms/sanitization";
import { useClientFileContext } from "../../contexts/ClientFileProvider";
import { useStoreContext } from "../../contexts/StoreProvider";

const UPDATE_CLIENT_FILE = gql`
  mutation UpdateClientFile($input: updateClientFileInput) {
    updateClientFile(input: $input) {
      clientFile {
        id
        locked_by {
          id
        }
        locked_at
      }
    }
  }
`;

const UPDATE_FORM = gql`
  mutation UpdateClientForm($input: updateClientFormInput) {
    updateClientForm(input: $input) {
      clientForm {
        id
        form_data
      }
    }
  }
`;

type PropsType = {
  pill?: boolean;
  className?: string;
  disabled?: boolean;
};

const DEBOUNCE_SAVE_DELAY_MS = 1000;

const Autosave = ({ pill, className, disabled }: PropsType) => {
  const { clientFile, formMode: mode } = useClientFileContext();
  const { isDirty } = useFormState();
  const { getValues } = useFormContext();
  const clientForm = clientFile.client_form;
  const { state: { user } } = useStoreContext();

  const watch = useWatch({
    name: [
      "section1",
      "section2",
      "section2a",
      "section2b",
      "section2c",
      "section2d",
      "section3",
      "section4Alt",
      "section4",
      "comments",
    ],
  });

  const [updateTime, setUpdateTime] = useState<Date>(
    new Date(clientForm.updated_at)
  );
  const [time, setTime] = useState<Date>(new Date());

  const [lockFile] = useMutation(UPDATE_CLIENT_FILE);

  const [updateForm, { error, loading }] = useMutation(UPDATE_FORM, {
    onCompleted: () => {
      setUpdateTime(new Date());
      // for safety, update lock state only if is not locked or is locked and same user
      // and status is Progress, Review or Action
      if (
        (!clientFile.locked_by || (clientFile.locked_by && clientFile.locked_by.id === user?.id)) &&
        ["Progress", "Review", "Action"].includes(clientFile.status) &&
        user?.role?.name !== "Sales Rep"
      ) {
        lockFile({
          variables: {
            input: {
              where: {
                id: clientFile.id,
              },
              data: {
                locked_at: new Date(),
                locked_by: user?.id,
              },
            },
          },
        });
      };
    },
    onError: () => {
      window.location.reload();
      return null;
    },
  });

  const updateData = useCallback(
    (newData) => {
      updateForm({
        variables: {
          input: {
            where: {
              id: clientForm.id,
            },
            data: {
              form_data: sanitizeUpdateClientFormData(newData)
            },
          },
        },
      });
      setTime(new Date());
    },
    [clientForm.id, updateForm]
  );

  const debouncedSave = useMemo(
    () =>
      debounce((newData: unknown) => {
        updateData(newData);
      }, DEBOUNCE_SAVE_DELAY_MS),
    [updateData]
  );

  useEffect(() => {
    if (watch && isDirty && mode !== "view" && !disabled) {
      debouncedSave(getValues());
    }
    return () => {
      debouncedSave.cancel();
    };
  }, [watch, debouncedSave, getValues, isDirty, mode, disabled]);

  useEffect(() => {
    const interval = setInterval(
      () => setTime(new Date()),
      process.env.REACT_APP_DEBUG_TOOLS === "ENABLE" ? 4000 : 60000
    ); // Updates once every 4 seconds (debug) or 1 Minute
    return () => {
      clearInterval(interval);
    };
  }, []);

  const content = (() => {
    if (loading) {
      return "Saving...";
    } else if (error) {
      return "Save failed";
    } else {
      const timeAgo = formatDistance(updateTime, time, {
        includeSeconds: process.env.REACT_APP_DEBUG_TOOLS === "ENABLE",
      })
        .concat(" ago")
        .replace("less than a minute ago", "just now");
      return `Last saved ${timeAgo}`;
    }
  })();

  const icon = (() => {
    if (loading) {
      return <Eye className={styles.icon} />;
    }
    return <Check className={styles.icon} />;
  })();

  return (
    <Text
      className={classNames(className, styles.text, {
        [styles.pill]: pill,
        [styles.autosaving]: loading,
        [styles.failed]: error,
      })}
    >
      {icon}
      {content}
    </Text>
  );
};

export default Autosave;
